// AddStudentDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Button, TextField, MenuItem, Alert, Snackbar, CircularProgress,
  Grid, Typography, Divider
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { isUrduText } from '../utils/urduFont';
import { addDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../Configuration';
import { allocateStudentId } from '../utils/allocateStudentId';
import { STUDENT_STATUS, getFatherNameFieldLabels } from '../utils/studentStatus';

const AddStudentDialog = ({ open, onClose }) => {
  const [newStudentName, setNewStudentName] = useState('');
  const [gender, setGender] = useState('');
  const [cnic, setCnic] = useState('');
  const [degree, setDegree] = useState('');
  const [status, setStatus] = useState(STUDENT_STATUS.ACTIVE);
  const [interview, setInterview] = useState('');
  const [scholarship, setScholarship] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [nameInUrdu, setNameInUrdu] = useState('');
  const [fatherNameInUrdu, setFatherNameInUrdu] = useState('');
  const [femaleNameRelation, setFemaleNameRelation] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [age, setAge] = useState('');
  const [address, setAddress] = useState('');
  const [phoneRes, setPhoneRes] = useState('');
  const [mobile, setMobile] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [profession, setProfession] = useState('');
  const [qualification, setQualification] = useState('');
  const [reason, setReason] = useState('');
  const [campus, setCampus] = useState('');
  const [campusDocId, setCampusDocId] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [isAnjumanMemberAtJoining, setIsAnjumanMemberAtJoining] = useState('');
  const [isAnjumanMemberAfterJoining, setIsAnjumanMemberAfterJoining] = useState('');
  const [isTanzeemMemberAtJoining, setIsTanzeemMemberAtJoining] = useState('');
  const [isTanzeemMemberAfterJoining, setIsTanzeemMemberAfterJoining] = useState('');
  const [courseTaaruf, setCourseTaaruf] = useState('');
  const [courseTaarufOther, setCourseTaarufOther] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCampuses = async () => {
    try {
      const campusSnapshot = await getDocs(collection(db, 'Campus'));
      let campusList = campusSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const allowedCampus = JSON.parse(localStorage.getItem('campusCode')) || [];
      if (allowedCampus.length > 0) {
        campusList = campusList.filter(campus => allowedCampus.includes(campus.campusCode));
      }
      setCampuses(campusList);
      if (campusList.length === 0) {
        showSnackbar('No campuses available for your access level. Please contact administrator.', 'warning');
      }
    } catch (error) {
      console.error('Error fetching campuses:', error);
      showSnackbar('Error loading campuses. Please try again.', 'error');
    }
  };

  useEffect(() => { fetchCampuses(); }, []);
  useEffect(() => {
    if (campuses.length === 1) {
      setCampus(campuses[0].campusCode);
      setCampusDocId(campuses[0].id);
    }
  }, [campuses]);

  const calculateAgeFromDob = (dob) => {
    if (!dob) return '';
    const parsed = new Date(dob);
    if (Number.isNaN(parsed.getTime())) return '';

    const today = new Date();
    let years = today.getFullYear() - parsed.getFullYear();
    const monthDiff = today.getMonth() - parsed.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parsed.getDate())) {
      years -= 1;
    }
    return years < 0 ? '' : String(years);
  };

  const handleDateOfBirthChange = (dob) => {
    setDateOfBirth(dob);
    // Auto-calculate age only when DOB is selected; otherwise keep age as-is
    if (dob) {
      setAge(calculateAgeFromDob(dob));
    }
  };

  const handleCampusChange = (campusCode) => {
    setCampus(campusCode);
    const c = campuses.find((x) => x.campusCode === campusCode);
    setCampusDocId(c?.id || '');
  };

  const validateForm = () => {
    if (!newStudentName.trim()) { showSnackbar('Student name is required', 'error'); return false; }
    if (gender !== 'Male' && gender !== 'Female') { showSnackbar('Gender is required to assign a student ID', 'error'); return false; }
    if (!campus) { showSnackbar('Please select a campus', 'error'); return false; }
    if (!campusDocId) { showSnackbar('Campus record is missing. Refresh and try again.', 'error'); return false; }
    const campusRow = campuses.find((x) => x.campusCode === campus);
    if (!campusRow || !(campusRow.studentIdPrefix || '').trim()) {
      showSnackbar('This campus has no Student ID prefix. Set it in Campus Management.', 'error');
      return false;
    }
    return true;
  };

  const handleAddStudent = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const newStudentId = await allocateStudentId(db, campusDocId, gender);
      const studentsRef = collection(db, 'Students');
      await addDoc(studentsRef, {
        name: newStudentName,
        id: newStudentId,
        gender, cnic, status, interview, scholarship,
        fatherName, nameInUrdu, fatherNameInUrdu,
        femaleNameRelation: gender === 'Female' ? femaleNameRelation : '',
        dateOfBirth,
        age, address, phoneRes, mobile, whatsapp, email,
        profession, qualification, reason,
        campusCode: campus,
        isAnjumanMemberAtJoining, isAnjumanMemberAfterJoining,
        isTanzeemMemberAtJoining, isTanzeemMemberAfterJoining,
        courseTaaruf,
        courseTaarufOther: courseTaaruf === 'Other' ? courseTaarufOther : '',
      });
      showSnackbar(`Student "${newStudentName}" added successfully`, 'success');
      onClose();
    } catch (error) {
      console.error('Error adding student: ', error);
      showSnackbar(error?.message || 'Error adding student. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const fatherLabels = getFatherNameFieldLabels(gender);

  const SectionHeader = ({ title }) => (
    <Grid item xs={12}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 0.5, mb: 0.5, borderColor: 'rgba(76,175,80,0.25)' }} />
    </Grid>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 700, py: 2 }}>
          Add New Student
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>

            {/* ── Basic Info ── */}
            <SectionHeader title="Basic Information" />
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Student Name *" fullWidth
                value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Name in Urdu" fullWidth
                value={nameInUrdu} onChange={(e) => setNameInUrdu(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontFamily: isUrduText(nameInUrdu) ? "'Noto Nastaliq Urdu','Jameel Noori Nastaleeq',serif" : 'inherit', direction: 'ltr', textAlign: 'left' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Gender *" select fullWidth
                value={gender}
                onChange={(e) => { const v = e.target.value; setGender(v); if (v !== 'Female') setFemaleNameRelation(''); }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="CNIC" fullWidth
                value={cnic} onChange={(e) => setCnic(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Status" select fullWidth value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value={STUDENT_STATUS.ACTIVE}>Active</MenuItem>
                <MenuItem value={STUDENT_STATUS.ACTIVE_LISTENER}>Active (listener)</MenuItem>
                <MenuItem value={STUDENT_STATUS.INACTIVE}>Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Campus *" select fullWidth
                value={campus}
                onChange={(e) => handleCampusChange(e.target.value)}
                disabled={campuses.length === 1}
                helperText={campus
                  ? (() => { const c = campuses.find(x => x.campusCode === campus); const p = c?.studentIdPrefix?.trim(); return p ? `ID e.g. ${p}-M001 / ${p}-F001` : 'Set prefix in Campus Management'; })()
                  : 'Select campus first'}
              >
                {campuses.map((c) => (
                  <MenuItem key={c.id} value={c.campusCode}>{c.campusName} ({c.campusCode})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date of Birth"
                format="DD/MM/YYYY"
                value={dateOfBirth ? dayjs(dateOfBirth) : null}
                onChange={(value) => handleDateOfBirthChange(value ? value.format('YYYY-MM-DD') : '')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                size="small"
                label="Age"
                type="number"
                fullWidth
                value={age}
                onChange={(e) => setAge(e.target.value)}
                helperText={dateOfBirth ? 'Auto-calculated from Date of Birth' : ''}
                inputProps={{ min: 0, max: 130 }}
              />
            </Grid>

            {/* ── Family ── */}
            <SectionHeader title="Family" />
            {gender === 'Female' && (
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="W/O or D/O" select fullWidth
                  value={femaleNameRelation} onChange={(e) => setFemaleNameRelation(e.target.value)}>
                  <MenuItem value="W/O">W/O (Wife of)</MenuItem>
                  <MenuItem value="D/O">D/O (Daughter of)</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField size="small" label={fatherLabels.english} fullWidth
                value={fatherName} onChange={(e) => setFatherName(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label={fatherLabels.urdu} fullWidth
                value={fatherNameInUrdu} onChange={(e) => setFatherNameInUrdu(e.target.value)}
                sx={{ '& .MuiInputBase-input': { fontFamily: isUrduText(fatherNameInUrdu) ? "'Noto Nastaliq Urdu','Jameel Noori Nastaleeq',serif" : 'inherit', direction: 'ltr', textAlign: 'left' } }}
              />
            </Grid>

            {/* ── Contact ── */}
            <SectionHeader title="Contact" />
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Mobile" fullWidth
                value={mobile} onChange={(e) => setMobile(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="WhatsApp" fullWidth
                value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Phone (Residence)" fullWidth
                value={phoneRes} onChange={(e) => setPhoneRes(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Email" type="email" fullWidth
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField size="small" label="Address" fullWidth
                value={address} onChange={(e) => setAddress(e.target.value)} />
            </Grid>

            {/* ── Academic & Program ── */}
            <SectionHeader title="Academic & Program" />
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Qualification" select fullWidth
                value={qualification} onChange={(e) => setQualification(e.target.value)}>
                <MenuItem value="Hifz">Hifz</MenuItem>
                <MenuItem value="Nazra">Nazra</MenuItem>
                <MenuItem value="Inter">Inter</MenuItem>
                <MenuItem value="Matric">Matric</MenuItem>
                <MenuItem value="Undergraduate">Undergraduate</MenuItem>
                <MenuItem value="Graduate">Graduate</MenuItem>
                <MenuItem value="Post Graduate">Post Graduate</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Profession" fullWidth
                value={profession} onChange={(e) => setProfession(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Interview" select fullWidth
                value={interview} onChange={(e) => setInterview(e.target.value)}>
                <MenuItem value="Passed">Passed</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Scholarship" select fullWidth
                value={scholarship} onChange={(e) => setScholarship(e.target.value)}>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Course Taaruf" select fullWidth
                value={courseTaaruf}
                onChange={(e) => { setCourseTaaruf(e.target.value); if (e.target.value !== 'Other') setCourseTaarufOther(''); }}>
                <MenuItem value="Daura">Daura</MenuItem>
                <MenuItem value="Social Media">Social Media</MenuItem>
                <MenuItem value="Rufqa-tanzeem">Rufqa-tanzeem</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            {courseTaaruf === 'Other' && (
              <Grid item xs={12} sm={6}>
                <TextField size="small" label="Course Taaruf (specify)" fullWidth
                  value={courseTaarufOther} onChange={(e) => setCourseTaarufOther(e.target.value)} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField size="small" label="Reason" fullWidth
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </Grid>

            {/* ── Membership ── */}
            <SectionHeader title="Membership" />
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Anjuman Member At Joining" select fullWidth
                value={isAnjumanMemberAtJoining} onChange={(e) => setIsAnjumanMemberAtJoining(e.target.value)}>
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Anjuman Member After Joining" select fullWidth
                value={isAnjumanMemberAfterJoining} onChange={(e) => setIsAnjumanMemberAfterJoining(e.target.value)}>
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Tanzeem Member At Joining" select fullWidth
                value={isTanzeemMemberAtJoining} onChange={(e) => setIsTanzeemMemberAtJoining(e.target.value)}>
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" label="Tanzeem Member After Joining" select fullWidth
                value={isTanzeemMemberAfterJoining} onChange={(e) => setIsTanzeemMemberAfterJoining(e.target.value)}>
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </TextField>
            </Grid>

            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddStudent}
            variant="contained"
            color="success"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 90 }}
          >
            {submitting ? 'Adding...' : 'Add Student'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddStudentDialog;
